import { GraduationCap, Smile, Type } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React, { useMemo } from "react";
import { Pressable } from "react-native";
import { type SheetProps, Text, XStack, YStack } from "tamagui";
import type { ModuleType } from "../../templates/types";
import { FormDataModal, FormSheetType } from "../core/components/FormDataModal";
import { hasHelpData } from "../core/screens/learndata/utils";
import { trackEvent } from "../core/trackEvent";
import {
  allModulesAvailable,
  type ModuleMetaCategory,
  type ModuleMetaData,
} from "../core/types";
import { PlanModelStep } from "../plans/PlanModel";
import { usePlanNavigation } from "../plans/usePlanNavigation";
import { Body } from "../ui/Body";
import { Group } from "../ui/Group";
import { IconBox } from "../ui/IconBox";
import { Label } from "../ui/Label";
import { Sheet } from "../ui/Sheet";
import { Subtext } from "../ui/Subtext";
import { TipsText } from "../ui/TipsText";
import { Title } from "../ui/Title";
import type SectionModel from "./SectionModel";
import { isCreateModule } from "./SectionUI";

export const CardsList: React.FC<{
  category: ModuleMetaCategory;
  onPress: (module: ModuleMetaData) => void;
  section: SectionModel;
  onClose: () => void;
}> = observer(({ category, onClose, onPress, section }) => {
  const cards = category.modules;

  const navigation = usePlanNavigation();

  const gotoLearnMore = React.useCallback(
    (type: ModuleType) => {
      trackEvent(`card_create_learn_${type}`);

      onClose(); // Close sheet
      navigation.navigate("Learn", {
        planUid: section.plan.uid,
        card: type,
        sectionUid: section.uid,
      });
    },
    [navigation, section, onClose],
  );

  const Icon = category.icon;

  return (
    <YStack gap="$space.small" marginBottom="$larger">
      {category.title && (
        <XStack gap="$medium" alignItems="center" marginLeft="$small">
          {Icon && <Icon color="$muted" />}
          <Label color={"$muted"}>{category.title}</Label>
        </XStack>
      )}
      <Group borderTop={!!category.title}>
        {cards.map((module, i) => {
          const isStaging = window?.location?.host.includes("--staging");
          const isDev = __DEV__ || isStaging;

          if (module.onlyInDev && !isDev) {
            // Skip until done
            return null;
          }

          const hasLearnMore = hasHelpData(module.type);

          return (
            <IconBox
              key={`_${i}`}
              onPress={async () => {
                onPress(module);
              }}
              grouped
              icon={() => (
                <Text lineHeight={24} fontSize={24}>
                  {module.icon}
                </Text>
              )}
              label={module.title}
              linkOrInfo={
                hasLearnMore ? (
                  <Pressable onPress={() => gotoLearnMore(module.type)}>
                    <XStack gap="$small" alignItems="center">
                      <GraduationCap color="$muted" size="$small" />
                      <Body muted textDecorationLine="underline">
                        Info
                      </Body>
                    </XStack>
                  </Pressable>
                ) : undefined
              }
            >
              <Subtext>{module.examples}</Subtext>
            </IconBox>
          );
        })}
      </Group>
    </YStack>
  );
});

export const AddCardSheet: React.FC<SheetProps & { section: SectionModel }> =
  observer(({ section, onOpenChange, ...props }) => {
    const navigation = usePlanNavigation();

    const [createModule, setCreateModule] = React.useState<
      ModuleMetaData | undefined
    >(undefined);

    const formValues = useMemo(
      () => ({ emoji: createModule?.icon ?? "" }),
      [createModule?.icon],
    );

    return (
      <>
        <Sheet
          sheetId="add_card"
          {...props}
          onOpenChange={onOpenChange}
          header={
            <YStack gap="$space.small">
              <Title>Add step</Title>
              <TipsText text="Select a step to start using it" />
            </YStack>
          }
        >
          {allModulesAvailable.map((moduleCategory, i) => (
            <CardsList
              key={`category_${i}`}
              category={moduleCategory}
              section={section}
              onPress={async (module) => {
                setCreateModule(module);
                onOpenChange?.(false);
              }}
              onClose={() => onOpenChange?.(false)}
            />
          ))}
        </Sheet>

        {!!createModule && (
          <FormDataModal
            onClose={() => setCreateModule(undefined)}
            visible={!!createModule}
            title={createModule?.title ?? ""}
            description={createModule?.description}
            form={[
              {
                formKey: "title",
                label: "Title",
                type: FormSheetType.Input,
                icon: Type,
                placeholder: "Title...",
              },
              {
                formKey: "emoji",
                label: "Icon",
                type: FormSheetType.Emoji,
                icon: Smile,
                placeholder: "Select icon...",
              },
              ...(createModule.extraData ?? []),
            ]}
            values={{ ...formValues, ...createModule.formValues }}
            onSave={async (data) => {
              if (!createModule || !isCreateModule(data)) {
                return;
              }

              const m = await section.addModule({
                type: createModule.type,
                doneType: createModule.doneType,
                isDraggable: true,
                initiated: true,
                ...(createModule.prepareData
                  ? createModule.prepareData(data)
                  : data),
              });

              trackEvent(`card_create_type_${createModule.type}`);

              section.plan.trackProgress(PlanModelStep.hasAddedCard, true);

              navigation.navigate("Module", {
                planUid: section.data.planUid,
                moduleUid: m.uid,
              });
            }}
          />
        )}
      </>
    );
  });
